import * as React from "react";
import {useTheme} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  Card,
  Divider,
  Grid,
  TextField,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  IconButton
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import {DataGrid, GridActionsCellItem} from "@mui/x-data-grid";
import {GET, POST} from "APIclient";
import {useEffect, useState} from "react";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {eachDayOfInterval} from "date-fns";
import properties from "constants/ApplicationConstants";

// Custom Pagination Component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666" }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor:
                  properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const columns = [
  {field: "id", headerName: "#", width: 30, headerAlign: "left", align: "left"},
  {
    field: "empName",
    headerName: "Employee Name",
    width: 150,
    headerAlign: "left",
    align: "left"
  },
  {field: "empId", headerName: "EMP ID", width: 80, headerAlign: "left", align: "left"},
  {
    field: "StartDate",
    headerName: "Start Date",
    width: 110,
    headerAlign: "left",
    align: "left"
  },
  {field: "EndDate", headerName: "End Date", width: 110, headerAlign: "left", align: "left"},
  {
    field: "AppliedOn",
    headerName: "Applied On",
    width: 110,
    headerAlign: "left",
    align: "left"
  },
  {
    field: "leaveType",
    headerName: "Leave Type",
    width: 100,
    headerAlign: "left",
    align: "center",
    renderCell: params => {
      return params.value === "MaternityLeave" ? "ML" : params.value;
    }
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: params => {
      const status = params.value.toLowerCase();
      let buttonColor;
      let statusText;

      switch (status) {
        case "pending":
          buttonColor = "warning";
          statusText = "Pending";
          break;
        case "approved":
          buttonColor = "success";
          statusText = "Approved";
          break;
        case "rejected":
          buttonColor = "error";
          statusText = "Rejected";
          break;
        case "cancelled":
          buttonColor = "secondary";
          statusText = "Cancelled";
          break;
      }

      return (
        <MDButton
          variant="contained"
          color={buttonColor}
          size="small"
          disabled
          sx={{
            textTransform: "capitalize",
            fontSize: "0.800rem",
            width: "70px",
            justifyContent: "center"
          }}
        >
          {statusText}
        </MDButton>
      );
    }
  },
  {
    field: "actions",
    headerName: "Action",
    width: 140,
    headerAlign: "left",
    align: "left",
    renderCell: params => {
      const status = params.row.status.toLowerCase();
      const isActionDisabled =
        status === "approved" ||
        status === "cancelled" ||
        status === "rejected" ||
        status === "reject";

      const isRejectDisabled = !(status === "pending" || status === "approved");

      return [
        <Tooltip title="Approve Leave" arrow>
          <GridActionsCellItem
            key={`approve-${params.id}`}
            icon={<DoneIcon />}
            label="Approve"
            color="success"
            onClick={() => params.row.handleApprove(params.row)}
            disabled={isActionDisabled}
          />
        </Tooltip>,
        <Tooltip title="Reject Leave" arrow>
          <GridActionsCellItem
            key={`reject-${params.id}`}
            icon={<ClearIcon />}
            label="Reject"
            color="error"
            onClick={() => params.row.handleReject(params.row)}
            disabled={isRejectDisabled}
          />
        </Tooltip>,
        <Tooltip title="View Details" arrow>
          <GridActionsCellItem
            key={`view-${params.id}`}
            icon={<VisibilityIcon />}
            label="View"
            color="primary"
            onClick={() => params.row.handleView(params.row)}
          />
        </Tooltip>
      ];
    }
  }
];

function getLeaveDates(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const dateArray = eachDayOfInterval({start, end});
  return dateArray.map(date => dayjs(date).format("YYYY-MM-DD"));
}

export default function LeaveApplicationReceived() {
  const empId = Number(window.sessionStorage.getItem("empId"));
  const theme = useTheme();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredLeaveRequests, setFilteredLeaveRequests] = useState([]);
  const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 20});
  const [totalItems, setTotalItems] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [statusFilter, setStatusFilter] = useState("");
  const [empIdFilter, setEmpIdFilter] = useState("");
  const [empNameFilter, setEmpNameFilter] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
  const [appliedEmpIdFilter, setAppliedEmpIdFilter] = useState("");
  const [appliedEmpNameFilter, setAppliedEmpNameFilter] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [actionType, setActionType] = useState("");
  const [reason, setReason] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openApproveRejectModal, setOpenApproveRejectModal] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [allDates, setAllDates] = useState([]);

  const handleClose = () => {
    setSnackOpen(false);
  };

  // FIX: Every branch below now explicitly sets `selectedDates`.
  // Previously the multi-day branch left `selectedDates` untouched, so a
  // stale value from a *previously opened* modal (e.g. a single-day request)
  // silently carried over into this request's approve/reject payload.
  useEffect(() => {
    if (selectedLeaveRequest) {
      let leaveDates = [];

      if (selectedLeaveRequest.leaveDates) {
        // FIX: strip the surrounding [ ] from the Java List.toString() format
        // (e.g. "[2026-08-27, 2026-08-28]") before splitting, otherwise the
        // first/last entries keep a stray bracket and can fail to parse.
        leaveDates = selectedLeaveRequest.leaveDates
          .replace(/[[\]]/g, "")
          .split(",")
          .map(date => dayjs(date.trim()).format("YYYY-MM-DD"))
          .filter(date => date !== "Invalid Date");
      } else {
        const {StartDate, EndDate} = selectedLeaveRequest;
        leaveDates = getLeaveDates(StartDate, EndDate).filter(date => {
          const day = new Date(date).getDay();
          return day !== 0 && day !== 6;
        });
      }

      setAllDates(leaveDates);

      if (leaveDates.length === 1) {
        setSelectedDates([leaveDates[0]]);
        setSelectAll(true);
      } else {
        // FIX: explicitly reset instead of leaving whatever was selected
        // for the previous request. Also fixed the "select all" check,
        // which used to compare a date count against a raw string length
        // and could never be true.
        setSelectedDates([]);
        setSelectAll(false);
      }
    } else {
      // FIX: clear everything when no request is selected (e.g. modal closed)
      setAllDates([]);
      setSelectedDates([]);
      setSelectAll(false);
    }
  }, [selectedLeaveRequest]);

  const handleDateChange = date => {
    setSelectedDates(prevSelectedDates =>
      prevSelectedDates.includes(date)
        ? prevSelectedDates.filter(d => d !== date)
        : [...prevSelectedDates, date]
    );
  };

  const handleSelectAllChange = event => {
    if (event.target.checked) {
      setSelectedDates(allDates);
    } else {
      setSelectedDates([]);
    }
    setSelectAll(event.target.checked);
  };

  const handleViewDetails = leaveRequest => {
    setSelectedLeaveRequest(leaveRequest);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLeaveRequest(null);
  };

  // FIX: reset selectedDates/selectAll/allDates immediately on open too,
  // as a second line of defense — don't rely solely on the useEffect timing.
  const handleApproveOpen = leaveRequest => {
    setActionType("approve");
    setSelectedDates([]);
    setSelectAll(false);
    setAllDates([]);
    setSelectedLeaveRequest(leaveRequest);
    setOpenApproveRejectModal(true);
  };

  const handleRejectOpen = leaveRequest => {
    setActionType("reject");
    setSelectedDates([]);
    setSelectAll(false);
    setAllDates([]);
    setSelectedLeaveRequest(leaveRequest);
    setOpenApproveRejectModal(true);
  };

  const handleApproveRejectClose = () => {
    setOpenApproveRejectModal(false);
    setSelectedLeaveRequest(null);
    setSelectedDates([]);
    setSelectAll(false);
    setAllDates([]);
    setReason("");
  };

  const handleApproveRejectSubmit = async () => {
    if (!reason) {
      setSnackMessage("Please provide a reason.");
      setSnackColor("error");
      setSnackOpen(true);
      return;
    }

    // FIX: guard against submitting with no dates selected at all — this
    // would previously silently send an empty leaveDates array.
    if (!selectedDates || selectedDates.length === 0) {
      setSnackMessage("Please select at least one date.");
      setSnackColor("error");
      setSnackOpen(true);
      return;
    }

    try {
      const url = `manager/${actionType}/${selectedLeaveRequest.leaveRequestId}/${selectedLeaveRequest.empId}`;
      const payload = {
        loggedInEmpId: empId,
        reason: reason,
        leaveDates: selectedDates
      };

      const response = await POST(url, payload);
      if (response.status === 200) {
        setSnackMessage(response.data.message);
        setSnackColor("success");
        setSnackOpen(true);
        updateLeaveRequestStatus(
          selectedLeaveRequest.leaveRequestId,
          actionType === "approve" ? "Approved" : "Rejected"
        );
        handleApproveRejectClose();
        fetchLeaveRequests(
          page,
          pageSize,
          appliedEmpIdFilter,
          appliedEmpNameFilter,
          appliedStatusFilter
        );
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) {
          setSnackMessage(error.response.data.message);
          setSnackColor("error");
          setSnackOpen(true);
        }
      } else {
        setSnackMessage(
          `Error ${actionType === "approve" ? "approving" : "rejecting"} the leave request.`
        );
        setSnackColor("error");
        setSnackOpen(true);
      }
    }
  };

  const updateLeaveRequestStatus = (leaveRequestId, status) => {
    setLeaveRequests(prev =>
      prev.map(req => (req.leaveRequestId === leaveRequestId ? {...req, status} : req))
    );
    setFilteredLeaveRequests(prev =>
      prev.map(req => (req.leaveRequestId === leaveRequestId ? {...req, status} : req))
    );
  };

  const fetchLeaveRequests = async (
    currentPage,
    currentPageSize,
    empIdFilter = "",
    empNameFilter = "",
    statusFilter = ""
  ) => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      let url = `manager/leave-requests/report-to?managerId=${empId}&page=${currentPage}&pageSize=${currentPageSize}`;
      if (empIdFilter) url += `&empId=${encodeURIComponent(empIdFilter)}`;
      if (empNameFilter) url += `&empName=${encodeURIComponent(empNameFilter)}`;
      if (statusFilter) {
        switch (statusFilter.toLowerCase()) {
          case "pending":
            url += `&status=1`;
            break;
          case "approved":
            url += `&status=0&approved=1`;
            break;
          case "rejected":
            url += `&status=2`;
            break;
          case "cancelled":
            url += `&cancel=1`;
            break;
          default:
            break;
        }
      }

      const response = await GET(url);

      if (response.status === 200) {
        const data = response.data.data.paginatedData || [];
        const totalRecords = response.data.data.totalItems || 0;
        const totalPagesCount = Math.ceil(totalRecords / currentPageSize) || 1;

        let index = currentPage * currentPageSize + 1;
        const formattedData = data.map(item => {
          let statusLabel = "";
          let startDate = dayjs(item.startDate).format("DD MMM YYYY");
          let endDate = item.endDate ? dayjs(item.endDate).format("DD MMM YYYY") : startDate;
          if (item.status === 1) {
            statusLabel = "Pending";
          } else if (item.approved === 1 && item.cancel === 0) {
            statusLabel = "Approved";
          } else if ((item.cancel === 1 && item.approved === 1) || item.cancel === 1) {
            statusLabel = "Cancelled";
          } else if (item.status === 2) {
            statusLabel = "Rejected";
          }
          const leaveType =
            item.leaveType === "MaternityLeave" ? "ML" : item.leaveType || "No Leave Type";
          return {
            id: index++,
            empId: item.empId,
            empName: `${item.firstName} ${item.lastName}`,
            StartDate: startDate,
            EndDate: endDate,
            AppliedOn: dayjs(item.addedOn).format("DD MMM YYYY"),
            leaveType: leaveType,
            status: statusLabel,
            reason: item.reason,
            leaveRequestId: item.leaveRequestId,
            leaveDates: item.leaveDates,
            handleApprove: handleApproveOpen,
            handleReject: handleRejectOpen,
            handleView: handleViewDetails
          };
        });

        setLeaveRequests(formattedData);
        setFilteredLeaveRequests(formattedData);
        setTotalItems(totalRecords);
        setTotalPages(totalPagesCount);
      }
    } catch (error) {
      console.error("Error fetching leave requests:", error);
      if (
        error.response &&
        (error.response.status === 400 ||
          error.response.status === 401 ||
          error.response.status === 403)
      ) {
        setSnackMessage(error.response.data.message || "Error fetching leave requests.");
        setSnackColor("error");
        setSnackOpen(true);
      } else {
        setSnackMessage("Error fetching leave requests.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchLeaveRequests(
      page,
      pageSize,
      appliedEmpIdFilter,
      appliedEmpNameFilter,
      appliedStatusFilter
    );
  }, []);

  const handleSearchClick = () => {
    setAppliedEmpIdFilter(empIdFilter);
    setAppliedEmpNameFilter(empNameFilter);
    setAppliedStatusFilter(statusFilter);
    setPage(0);
    setPaginationModel({page: 0, pageSize: pageSize});
    fetchLeaveRequests(0, pageSize, empIdFilter, empNameFilter, statusFilter);
  };

  const handleFilterChange = field => e => {
    if (field === "empId") setEmpIdFilter(e.target.value);
    if (field === "empName") setEmpNameFilter(e.target.value);
    if (field === "status") setStatusFilter(e.target.value);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setPaginationModel(prev => ({...prev, page: newPage}));
    fetchLeaveRequests(newPage, pageSize, appliedEmpIdFilter, appliedEmpNameFilter, appliedStatusFilter);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      setPaginationModel(prev => ({...prev, page: lastPageIndex}));
      await fetchLeaveRequests(lastPageIndex, pageSize, appliedEmpIdFilter, appliedEmpNameFilter, appliedStatusFilter);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} pb={2} px={3}>     
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 1.5}}>
                <Typography
                  sx={{
                    gridColumn: "span 3",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Leave Application Received
                </Typography>
                <TextField
                  sx={{gridColumn: "span 2"}}
                  label="Employee ID"
                  value={empIdFilter}
                  onChange={handleFilterChange("empId")}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  sx={{gridColumn: "span 2"}}
                  label="Employee Name"
                  value={empNameFilter}
                  onChange={handleFilterChange("empName")}
                  fullWidth
                  variant="outlined"
                />
                <FormControl sx={{gridColumn: "span 2"}}>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={statusFilter}
                    label="Status"
                    onChange={handleFilterChange("status")}
                    variant="outlined"
                    sx={{
                      height: "45px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{gridColumn: "span 2", background: properties.colors.bgcolorbluethem}}
                  variant="contained"
                  color="info"
                  onClick={handleSearchClick}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
            </Card>
        </AppBar>
        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredLeaveRequests}
              columns={columns}
              paginationMode="server"
              rowCount={totalItems}
              pageSizeOptions={[20]}
              paginationModel={paginationModel}
              loading={isFetching}
              hideFooter
              size="small"
              sx={{
                fontSize:400,
                fontSize: "14px",
                fontFamily: properties.fontFamily,
                height: "450px",
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important",
                },
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalItems}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={isFetching}
            />
          </Paper>
        </MDBox>

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>
              Leave Application Details
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{padding: 2}}>
            {selectedLeaveRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedLeaveRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedLeaveRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Applied On"
                      value={selectedLeaveRequest.AppliedOn || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Start Date"
                      value={selectedLeaveRequest.StartDate || "No Start Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="End Date"
                      value={selectedLeaveRequest.EndDate || "No End Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Leave Type"
                      value={selectedLeaveRequest.leaveType || "No Leave Type Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedLeaveRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      value={selectedLeaveRequest.reason || "No reason provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Divider sx={{my: 2}} />
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseModal} variant="contained">
              Close
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openApproveRejectModal}
          onClose={handleApproveRejectClose}
          fullWidth
          maxWidth="sm"
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle>
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>
              {actionType === "approve" ? "Approve" : "Reject"} Leave Request
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{padding: 2}}>
            {selectedLeaveRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedLeaveRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedLeaveRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Applied On"
                      value={selectedLeaveRequest.AppliedOn || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Leave Type"
                      value={selectedLeaveRequest.leaveType || "No Leave Type Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedLeaveRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div>
                      <FormControlLabel
                        control={<Checkbox checked={selectAll} onChange={handleSelectAllChange} />}
                        label="Select All Dates"
                      />
                      <Grid container justifyContent="flex-start">
                        {allDates &&
                          allDates.map((date, index) => (
                            <Grid
                              item
                              xs={3}
                              key={index}
                              container
                              alignItems="center"
                              justifyContent="flex-start"
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedDates.includes(date)}
                                    onChange={() => handleDateChange(date)}
                                  />
                                }
                                label={dayjs(date).format("DD-MM-YYYY")}
                              />
                            </Grid>
                          ))}
                      </Grid>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={
                        <span>
                          Reason<span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      multiline
                      rows={1}
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                  <Divider sx={{my: 2}} />
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleApproveRejectClose} variant="contained">
              Cancel
            </MDButton>
            <MDButton color="primary" onClick={handleApproveRejectSubmit} variant="contained">
              Submit
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Snackbar
        open={snackOpen}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={snackColor === "success" ? "success" : "error"}
          variant="filled"
          sx={{width: "100%", color: "#fff"}}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}